import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { Dialog } from '@node-space/ui-components'
import { useProtocolContext } from '@/app/context/ProtocolContext'
import { useSelectedItemContext } from '@/app/context/SelectedItemContext'
import orderBy from 'lodash/orderBy'
import { Currency, isBvnkCurrency } from '@/types/currency'
import ModalMobile, { ModalMobileSize } from '../modal/ModalMobile'
import { ProtocolDialogContent } from './ProtocolDialogContent'

interface PaymentSelectProps {
  paymentSummaryProtocols: string[]
  currencies: Currency[]
  selectedCurrencyCode: string
  isMultiProtocolCurrency: boolean
  paymentProtocol: string
  isMobile?: boolean
  showProtocolModal?: boolean
  setShowProtocolModal?: Dispatch<SetStateAction<boolean>>
}

export const PaymentSelectProtocol = ({
  paymentSummaryProtocols,
  paymentProtocol,
  selectedCurrencyCode,
  currencies,
  isMultiProtocolCurrency,
  isMobile,
  showProtocolModal,
  setShowProtocolModal,
}: PaymentSelectProps) => {
  const t = useTranslations()
  const { protocol, setProtocol } = useProtocolContext()
  const { item, setItem } = useSelectedItemContext()

  const multiProtocols = useMemo(() => {
    if (!isMultiProtocolCurrency) {
      return []
    }

    const filteredCurrencies = currencies?.filter(currency => {
      if (isBvnkCurrency(currency)) {
        return (
          selectedCurrencyCode === currency?.code?.toLowerCase() &&
          currency?.enabledProtocols?.length > 1
        )
      }
      // Layer1 currencies don't have multiple protocols, so return empty for multi-protocol check
      return false
    })

    const currencyProtocols = filteredCurrencies
      ?.map(currency => {
        if (isBvnkCurrency(currency)) {
          return currency?.enabledProtocols
        }
        return []
      })
      .flat()

    const networkMap: { [key: string]: string } = {}
    currencyProtocols?.forEach(currencyProtocol => {
      if (currencyProtocol) {
        networkMap[currencyProtocol?.code] = currencyProtocol?.network
      }
    })

    const protocols = paymentSummaryProtocols?.map(paymentSummaryProtocol => {
      const network = networkMap[paymentSummaryProtocol] || ''
      return { code: paymentSummaryProtocol, network }
    })

    return orderBy(protocols, ['network']) || []
  }, [currencies, isMultiProtocolCurrency, paymentSummaryProtocols, selectedCurrencyCode])

  const optionsForSelect = multiProtocols
    ?.map(currency => ({
      label: currency?.code,
      value: currency?.code?.toLowerCase(),
      network: currency?.network,
    }))
    .flat()

  const dialogTitle = t('chooseProtocolFor', {
    currencyCode: selectedCurrencyCode?.toUpperCase(),
  })

  useEffect(() => {
    if (multiProtocols?.length && !protocol?.length) {
      setProtocol(paymentProtocol?.toLowerCase())
      setItem({ ...item, protocol: paymentProtocol?.toLowerCase() })
    }
  }, [multiProtocols, paymentProtocol, protocol, setProtocol])

  return (
    optionsForSelect?.length > 0 &&
    isMultiProtocolCurrency && (
      <>
        {isMobile ? (
          <ModalMobile
            disabled={false}
            isOpen={showProtocolModal}
            onClose={() => setShowProtocolModal?.(false)}
            body={
              <ProtocolDialogContent
                optionsForSelect={optionsForSelect}
                selectedCurrencyCode={selectedCurrencyCode}
                setShowProtocolModal={setShowProtocolModal}
                setProtocol={setProtocol}
                isMobile={isMobile}
              />
            }
            modalSize={ModalMobileSize.medium}
            removeModalHeader={true}
          />
        ) : (
          <Dialog
            title={dialogTitle}
            showClose={false}
            open={showProtocolModal ?? false}
            onClose={() => setShowProtocolModal?.(false)}
            type="utility"
          >
            <ProtocolDialogContent
              optionsForSelect={optionsForSelect}
              selectedCurrencyCode={selectedCurrencyCode}
              setShowProtocolModal={setShowProtocolModal}
              setProtocol={setProtocol}
              isMobile={isMobile}
            />
          </Dialog>
        )}
      </>
    )
  )
}
